// --------------------------------------------------------------
// Created On: 2026-10-27
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import AttributeHighlightModal from "./AttributeHighlightModal/AttributeHighlightModal";
import styles from "./SystemControlAttributeEditRow.module.scss";

// Table row for editing a single system control attribute, which can be dragged to a new position in the table.
export default function SystemControlAttributeEditRow(props: Props): Component {
  const [showHighlightModal, setShowHighlightModal] = useState<boolean>(false);
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDraggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: `attribute-${props.index}`, disabled: props.isFixed === true });

  return (
    <tr
      data-test="component-attribute-row"
      className={`${styles.row} ${isDragging ? styles.dragging : ""}`}
      ref={setDraggableNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
    >
      {props.isFixed !== true && (
        <td
          data-test="component-attribute-drag-handle"
          className={styles.handleCell}
          title="Drag to reorder"
          {...dragAttributes}
          {...dragListeners}
        />
      )}

      <td>
        <input
          data-test="component-attribute-input"
          className="form-control"
          list={props.datalistId}
          value={props.attributeName}
          onChange={(e) => props.onChangeAttribute(e.target.value)}
        />
      </td>

      <td className={styles.shrinkCell}>
        <button
          data-test="component-attribute-highlight-button"
          type="button"
          className={`btn btn-primary ${styles.highlightButton}`}
          title="Highlight colors"
          onClick={() => setShowHighlightModal(true)}
        >
          <i className="fa fa-fw fa-paint-brush fa-xs" />
        </button>
        {props.isFixed !== true && (
          <button
            data-test="component-attribute-delete-button"
            type="button"
            className="btn btn-danger"
            onClick={() => props.onRemoveAttribute()}
          >
            <i className="fa fa-fw fa-times fa-xs" />
          </button>
        )}
        <AttributeHighlightModal
          showModal={showHighlightModal}
          attributeName={props.attributeName}
          units={props.units}
          isBoolean={props.isBoolean}
          isHighlightable={props.isHighlightable}
          regEnumId={props.regEnumId}
          enumerationMap={props.enumerationMap}
          highlightingRules={props.highlightingRules}
          onSave={(highlightingRules) => {
            props.onChangeHighlightingRules(highlightingRules);
            setShowHighlightModal(false);
          }}
          onClose={() => setShowHighlightModal(false)}
        />
      </td>
    </tr>
  );
}

interface Props {
  index: number;
  isFixed?: boolean;
  datalistId: string;
  attributeName: string;
  units: string;
  isBoolean: boolean;
  isHighlightable: boolean;
  regEnumId: number | null;
  enumerationMap: EnumerationMap;
  highlightingRules: HighlightingRule[];
  onChangeAttribute: (attributeName: string) => void;
  onChangeHighlightingRules: (highlightingRules: HighlightingRule[]) => void;
  onRemoveAttribute: () => void;
}

interface HighlightingRule {
  comparator: string;
  value: string;
  valueType: string;
  colorHexCode: string;
  sortPriority: number;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}
